import React, { useCallback, useEffect, useState } from 'react';
import {
    Autocomplete,
    Avatar,
    Box,
    CircularProgress,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    TextField,
    Typography,
} from '@mui/material';
import TrashIconButton from 'app/shared/widgets/TrashIconButton';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ConfirmDialog from 'app/shared/widgets/ConfirmDialog';
import companyServices from 'app/services/company-services';
import { useSnackbar } from 'notistack';
import _ from 'lodash';

const RetailCustomersSettings = ({ companyId }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [retailCustomers, setRetailCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchOptions, setSearchOptions] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [confirmRemove, setConfirmRemove] = useState(null);

    useEffect(() => {
        if (!companyId) return;
        companyServices.getRetailCustomers(companyId)
            .then(setRetailCustomers)
            .finally(() => setLoading(false));
    }, [companyId]);

    const fetchCandidates = useCallback(
        _.debounce((q) => {
            if (!q || q.length < 2) {
                setSearchOptions([]);
                setSearchLoading(false);
                return;
            }
            setSearchLoading(true);
            companyServices.searchRetailCandidates(companyId, q)
                .then(setSearchOptions)
                .finally(() => setSearchLoading(false));
        }, 300),
        [companyId]
    );

    const handleInputChange = (_, value) => {
        setInputValue(value);
        fetchCandidates(value);
    };

    const handleSelect = (_, customer) => {
        if (!customer) return;
        companyServices.addRetailCustomer(companyId, customer.id)
            .then((updated) => {
                setRetailCustomers(updated);
                setInputValue('');
                setSearchOptions([]);
            })
            .catch(() => enqueueSnackbar('Error al agregar cliente', { variant: 'error' }));
    };

    const handleRemove = (customerId) => {
        setConfirmRemove(null);
        companyServices.removeRetailCustomer(companyId, customerId)
            .then((updated) => setRetailCustomers(updated))
            .catch(() => enqueueSnackbar('Error al eliminar cliente', { variant: 'error' }));
    };

    if (loading) {
        return <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={24} /></Box>;
    }

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Los clientes agregados aquí tendrán la etiqueta <strong>Retail</strong> en sus pedidos.
            </Typography>

            <Autocomplete
                inputValue={inputValue}
                onInputChange={handleInputChange}
                onChange={handleSelect}
                options={searchOptions}
                getOptionLabel={(o) => o.name || ''}
                renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                        <Box>
                            <Typography variant="body2">{option.name}</Typography>
                            {option.code && (
                                <Typography variant="caption" color="text.secondary">{option.code}</Typography>
                            )}
                        </Box>
                    </li>
                )}
                loading={searchLoading}
                filterOptions={(x) => x}
                noOptionsText={inputValue.length < 2 ? 'Escribe para buscar...' : 'Sin resultados'}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Buscar cliente por nombre o código"
                        size="small"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {searchLoading && <CircularProgress size={16} />}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
                sx={{ mb: 2 }}
            />

            {retailCustomers.length === 0 ? (
                <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', py: 2 }}>
                    No hay clientes retail configurados
                </Typography>
            ) : (
                <List dense disablePadding>
                    {retailCustomers.map((c) => (
                        <ListItem
                            key={c.id}
                            disableGutters
                            secondaryAction={
                                <TrashIconButton title="Eliminar de clientes retail" onClick={() => setConfirmRemove(c)} />
                            }
                        >
                            <ListItemAvatar sx={{ minWidth: 36 }}>
                                <Avatar sx={{ width: 28, height: 28, bgcolor: 'state_invoiced.main' }}>
                                    <PersonOutlineIcon sx={{ fontSize: 16 }} />
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={c.name}
                                secondary={c.code || null}
                                primaryTypographyProps={{ variant: 'body2' }}
                                secondaryTypographyProps={{ variant: 'caption' }}
                            />
                        </ListItem>
                    ))}
                </List>
            )}

            <ConfirmDialog
                open={Boolean(confirmRemove)}
                title="Eliminar cliente retail"
                message={
                    <>
                        ¿Estás seguro que deseas eliminar a <strong>{confirmRemove?.name}</strong> de los clientes retail?
                        <br />
                        Sus pedidos dejarán de marcarse como retail.
                    </>
                }
                onClose={() => setConfirmRemove(null)}
                onConfirm={() => handleRemove(confirmRemove.id)}
            />
        </Box>
    );
};

export default RetailCustomersSettings;
